<script setup lang="ts">
interface Props {
  media: Media[]
}

defineProps<Props>()

const open = defineModel<boolean>('open', { default: false })
const activeIndex = defineModel<number>('activeIndex', { default: 0 })
const view = defineModel<'carousel' | 'grid'>('view', { default: 'carousel' })

const selectFromGrid = (index: number) => {
  activeIndex.value = index
  view.value = 'carousel'
}

useEscapeLayer(open, () => {
  open.value = false
})
</script>

<template>
  <DialogRoot v-model:open="open">
    <DialogPortal>
      <DialogOverlay data-overlay-layer class="fixed inset-0 z-modal-mask bg-white data-[state=open]:animate-lightboxIn data-[state=closed]:animate-lightboxOut" />

      <DialogContent
        data-overlay-layer
        class="fixed inset-0 z-modal flex flex-col outline-none data-[state=open]:animate-lightboxIn data-[state=closed]:animate-lightboxOut"
        :class="{
          'h-dvh overflow-hidden': view === 'carousel',
          'overflow-y-auto': view === 'grid',
        }"
        @open-auto-focus.prevent
      >
        <VisuallyHidden>
          <DialogTitle>{{ $t('labels.gallery') }}</DialogTitle>
        </VisuallyHidden>

        <DialogClose
          type="button"
          class="group absolute z-2 top-[var(--unit--vertical)] right-[var(--unit--horizontal)] icon-box-cross"
        >
          <svgo-cross class="w-full overflow-visible stroke-accent stroke-default group-hover:stroke-black icon-hover" />
        </DialogClose>

        <div
          class="flex-1 flex flex-col"
          :class="{
            'overflow-hidden': view === 'carousel'
          }"
        >
          <transition
            mode="out-in"
            enter-active-class="transition-opacity duration-150 ease-out"
            enter-from-class="opacity-0"
            leave-active-class="transition-opacity duration-150 ease-in"
            leave-to-class="opacity-0"
          >
            <core-media-preview-carousel
              v-if="view === 'carousel'"
              v-model="activeIndex"
              :media="media"
            >
              <template #actions>
                <button
                  v-if="media.length > 1"
                  type="button"
                  class="text-sm text-hover-accent"
                  @click="view = 'grid'"
                >
                  {{ $t('labels.viewAllImages') }}
                </button>
              </template>
            </core-media-preview-carousel>

            <core-media-preview-grid
              v-else
              :media="media"
              @select="selectFromGrid"
              class="px-1 pb-1 pt-[calc(var(--unit--vertical)*2+var(--lh))]"
            />
          </transition>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
